import { CompositionDto } from "../editor.ts";
import { AnimatedImageDto } from "../animated-image.ts";
import { SourceImageDto } from "../source-image.ts";
import { EndSceneDto, SceneTypeDto, StartSceneDto } from "../overlays.ts";
import { CreativeProjectTimelineItem } from "../creative-project.ts";

export function getAnimatedImageConfig(
  timelineItem: CreativeProjectTimelineItem,
) {
  if (!timelineItem.animatedImageExternalId) {
    throw new Error("Animated image external ID is not set");
  }

  const config =
    timelineItem.animatedImageConfigs[timelineItem.animatedImageExternalId];

  if (!config) {
    throw new Error("Animated image config is not set");
  }

  return config;
}

type SetCompositionAnimatedImageProps = {
  timelineItem: CreativeProjectTimelineItem;
  animatedImageExternalId: string;
  duration?: number;
  volume?: number;
};

export function setCompositionAnimatedImage({
  timelineItem,
  animatedImageExternalId,
  duration = 2.5,
  volume,
}: SetCompositionAnimatedImageProps) {
  timelineItem.animatedImageExternalId = animatedImageExternalId;

  if (
    !timelineItem.animatedImageExternalIds.includes(animatedImageExternalId)
  ) {
    timelineItem.animatedImageExternalIds.push(animatedImageExternalId);
  }

  if (!timelineItem.animatedImageConfigs[animatedImageExternalId]) {
    timelineItem.animatedImageConfigs[animatedImageExternalId] = {
      reverse: false,
      startAt: 0,
      duration,
      volume,
      playbackSpeed: 1,
      cropConfigs: {},
    };
  }
}

export function isAnimating(animatedImage: AnimatedImageDto) {
  return (
    animatedImage.type === "GENERATED" &&
    !["COMPLETED", "FAILED"].includes(animatedImage.status)
  );
}

export function isProcessingUploadedVideo(animatedImage: AnimatedImageDto) {
  return (
    animatedImage.type === "UPLOADED" &&
    !["COMPLETED", "FAILED"].includes(animatedImage.status)
  );
}

export function isProcessingSlowMotion(animatedImage: AnimatedImageDto) {
  return !["COMPLETED", "FAILED", ""].includes(animatedImage.smStatus ?? "");
}

export function isSourceImageProcessing(sourceImage: SourceImageDto) {
  return !["COMPLETED", "FAILED"].includes(sourceImage.status);
}

export function getElementsByScene(
  composition: CompositionDto | undefined,
  sceneType: SceneTypeDto,
) {
  if (!composition) {
    return [];
  }

  if (sceneType === "mainScene") {
    return composition.elements?.[composition.aspectRatio] ?? [];
  }

  return composition[sceneType]?.[composition.aspectRatio]?.elements ?? [];
}

const resolutions: Record<string, { width: number; height: number }> = {
  "16:9": { width: 1920, height: 1080 },
  "9:16": { width: 1080, height: 1920 },
  "1:1": { width: 1080, height: 1080 },
};

export const getResolution = (aspectRatio: string) => {
  const resolution = resolutions[aspectRatio as keyof typeof resolutions];

  if (!resolution) {
    throw new Error(`Invalid aspect ratio: ${aspectRatio}`);
  }

  return resolution;
};

export const parseAspectRatio = (aspectRatio: string) => {
  const parts = aspectRatio.split(":");
  return parseInt(parts[0]!) / parseInt(parts[1]!);
};

export const getStartScene = (
  composition: CompositionDto,
): StartSceneDto | null => {
  return composition.startScene?.[composition.aspectRatio] ?? null;
};

export const getEndScene = (
  composition: CompositionDto,
): EndSceneDto | null => {
  return composition.endScene?.[composition.aspectRatio] ?? null;
};

export function getElementsByAspectRatio(
  composition: CompositionDto,
  sceneType: SceneTypeDto,
) {
  if (sceneType === "mainScene") {
    return composition.elements?.[composition.aspectRatio] ?? [];
  }

  return composition[sceneType]?.[composition.aspectRatio]?.elements ?? [];
}

export function getAssetMediaExternalIds(
  composition: CompositionDto,
  excludeAssetMediaExternalIds: string[] = [],
) {
  const excludeAssetMediaExternalIdsSet = new Set(excludeAssetMediaExternalIds);

  const aspectRatio = composition.aspectRatio;

  const mainSceneElements = composition.elements?.[aspectRatio] ?? [];
  const startSceneElements =
    composition.startScene?.[aspectRatio]?.elements ?? [];
  const endSceneElements = composition.endScene?.[aspectRatio]?.elements ?? [];

  const idsMainScene = mainSceneElements
    .filter((element) => element.type === "IMAGE" || element.type === "VIDEO")
    .filter(
      (element) =>
        !excludeAssetMediaExternalIdsSet.has(element.assetMediaExternalId),
    )
    .map((element) => element.assetMediaExternalId);

  const idsStartScene = startSceneElements
    .filter((element) => element.type === "IMAGE" || element.type === "VIDEO")
    .filter(
      (element) =>
        !excludeAssetMediaExternalIdsSet.has(element.assetMediaExternalId),
    )
    .map((element) => element.assetMediaExternalId);

  const idsEndScene = endSceneElements
    .filter((element) => element.type === "IMAGE" || element.type === "VIDEO")
    .filter(
      (element) =>
        !excludeAssetMediaExternalIdsSet.has(element.assetMediaExternalId),
    )
    .map((element) => element.assetMediaExternalId);

  const assetMediaExternalIds = new Set([
    ...idsMainScene,
    ...idsStartScene,
    ...idsEndScene,
  ]);

  return Array.from(assetMediaExternalIds);
}

export function getFontExternalIds(composition: CompositionDto): string[] {
  const externalIds = new Set<string>();

  const elements = [
    ...Object.values(composition.elements ?? {}).flat(),
    ...Object.values(composition.startScene ?? {}).flatMap(
      (scene) => scene.elements,
    ),
    ...Object.values(composition.endScene ?? {}).flatMap(
      (scene) => scene.elements,
    ),
  ];

  for (const element of elements) {
    if (element.type === "TEXT" && element.typography.fontExternalId) {
      externalIds.add(element.typography.fontExternalId);
    }
  }

  const captions = composition.captions;
  if (captions && "config" in captions) {
    const fontExternalId = captions.config.style.text?.fontExternalId;
    if (fontExternalId) {
      externalIds.add(fontExternalId);
    }
  }

  return Array.from(externalIds);
}

export type EnsureAspectRatioProps = {
  lockAspectRatio: boolean;
  aspectRatio: number | null;
  fieldName: "w" | "h";
  newValue: number;
};

export type EnsureAspectRatioResult = {
  w?: number;
  h?: number;
};

export function ensureAspectRatio({
  lockAspectRatio,
  aspectRatio,
  fieldName,
  newValue,
}: EnsureAspectRatioProps): EnsureAspectRatioResult {
  const result: EnsureAspectRatioResult = {};

  if (fieldName === "w") {
    result.w = newValue;
  } else {
    result.h = newValue;
  }

  if (aspectRatio === null || !lockAspectRatio) {
    return result;
  }

  if (fieldName === "w") {
    result.h = newValue / aspectRatio;
  } else {
    result.w = newValue * aspectRatio;
  }

  return result;
}

export function getCompositionExternalIds(composition: CompositionDto) {
  const sourceImageExternalIds = new Set<string>();
  const animatedImageExternalIds = new Set<string>();
  const voiceOverExternalIds = new Set(composition.voiceOverExternalIds ?? []);
  const backgroundMusicExternalIds = new Set<string>();

  if (composition.voiceOver) {
    voiceOverExternalIds.add(composition.voiceOver.externalId);
  }

  if (composition.backgroundMusic) {
    backgroundMusicExternalIds.add(composition.backgroundMusic.externalId);
  }

  for (const item of composition.mainSceneTimeline) {
    for (const id of item.sourceImageExternalIds) {
      sourceImageExternalIds.add(id);
    }
    if (item.startSourceImageExternalId) {
      sourceImageExternalIds.add(item.startSourceImageExternalId);
    }
    if (item.endSourceImageExternalId) {
      sourceImageExternalIds.add(item.endSourceImageExternalId);
    }
    if (item.animatedImageExternalId) {
      animatedImageExternalIds.add(item.animatedImageExternalId);
    }
    for (const id of item.animatedImageExternalIds) {
      animatedImageExternalIds.add(id);
    }
  }

  return {
    sourceImageExternalIds: Array.from(sourceImageExternalIds),
    animatedImageExternalIds: Array.from(animatedImageExternalIds),
    voiceOverExternalIds: Array.from(voiceOverExternalIds),
    backgroundMusicExternalIds: Array.from(backgroundMusicExternalIds),
  };
}

export function getBackgroundMusicVolume(composition: CompositionDto) {
  let volume = composition.backgroundMusic?.volume;

  if (volume === undefined) {
    volume = getBackgroundMusicInitialVolume(composition);
  }

  return volume;
}

// Legacy fallback for compositions saved before new tracks stored an explicit volume.
export function getBackgroundMusicInitialVolume(composition: CompositionDto) {
  const hasVoiceOver = composition.voiceOver !== undefined;

  return hasVoiceOver ? 0.3 : 1;
}

export function isAutoDuckingEnabled(composition: CompositionDto) {
  return composition.autoDucking ?? false;
}

export function createCompositionBackgroundMusic(
  composition: CompositionDto,
  externalId: string,
): NonNullable<CompositionDto["backgroundMusic"]> {
  return {
    externalId,
    volume: composition.backgroundMusic?.volume ?? 1,
  };
}
